/**
 * 🌍 語言路由工具 - 優化版
 * 
 * 提供 URL-based 國際化支援，讓每種語言有獨立的路徑前綴
 * 例如：/en/features, /zh/dashboard, /ja/about
 * 
 * 🚀 優化：
 * - 快取正則表達式
 * - 減少字串操作
 * - 提供更高效的路徑解析
 */

// 支援的語言列表
export const SUPPORTED_LANGUAGES = ['en', 'zh', 'ja'];

// 預設語言（改為英文）
export const DEFAULT_LANGUAGE = 'en';

// 語言名稱對照表（用於 UI 顯示）
export const LANGUAGE_NAMES = {
  en: 'English',
  zh: '繁體中文',
  ja: '日本語'
};

// 🚀 快取正則表達式
const LANGUAGE_PREFIX_REGEX = /^\/(en|zh|ja)(\/|$)/;

/**
 * 從 URL 路徑中提取語言代碼
 * @param {string} pathname - 當前路徑（例如：/en/features）
 * @returns {string} - 語言代碼（en, zh, ja）
 */
export const extractLanguageFromPath = (pathname) => {
  const match = pathname.match(LANGUAGE_PREFIX_REGEX);
  return match ? match[1] : DEFAULT_LANGUAGE;
};

/**
 * 檢查路徑是否包含語言前綴
 * @param {string} pathname - 路徑
 * @returns {boolean}
 */
export const hasLanguagePrefix = (pathname) => {
  return LANGUAGE_PREFIX_REGEX.test(pathname);
};

/**
 * 從 URL 路徑中移除語言前綴
 * @param {string} pathname - 當前路徑
 * @returns {string} - 移除語言前綴後的路徑
 */
export const removeLanguagePrefix = (pathname) => {
  if (!hasLanguagePrefix(pathname)) {
    return pathname;
  }
  
  // 使用正則替換移除語言前綴
  const cleanPath = pathname.replace(LANGUAGE_PREFIX_REGEX, '/');
  return cleanPath === '' ? '/' : cleanPath;
};

/**
 * 為路徑添加語言前綴
 * @param {string} path - 原始路徑（例如：/features）
 * @param {string} language - 語言代碼
 * @returns {string} - 帶語言前綴的路徑（例如：/en/features）
 */
export const addLanguagePrefix = (path, language) => {
  // 確保語言有效
  const lang = SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;
  
  // 移除現有的語言前綴（如果有）
  const cleanPath = removeLanguagePrefix(path);
  
  // 處理根路徑
  if (cleanPath === '/' || cleanPath === '') {
    return `/${lang}`;
  }
  
  // 確保路徑以 / 開頭
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  
  // 添加語言前綴
  return `/${lang}${normalizedPath}`;
};

/**
 * 切換路徑的語言
 * @param {string} currentPath - 當前完整路徑
 * @param {string} newLanguage - 目標語言
 * @returns {string} - 新的路徑
 */
export const switchPathLanguage = (currentPath, newLanguage) => {
  const cleanPath = removeLanguagePrefix(currentPath);
  return addLanguagePrefix(cleanPath, newLanguage);
};

/**
 * 標準化路徑 - 確保所有路徑都有語言前綴
 * @param {string} pathname - 原始路徑
 * @param {string} fallbackLanguage - 如果沒有語言前綴，使用的備用語言
 * @returns {string} - 標準化後的路徑
 */
export const normalizePath = (pathname, fallbackLanguage = DEFAULT_LANGUAGE) => {
  if (hasLanguagePrefix(pathname)) {
    return pathname;
  }
  return addLanguagePrefix(pathname, fallbackLanguage);
};

/**
 * 取得當前語言的首頁路徑
 * @param {string} language - 語言代碼
 * @returns {string}
 */
export const getHomePathForLanguage = (language) => {
  return `/${SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE}`;
};

/**
 * 解析完整的路徑資訊（優化版）
 * @param {string} pathname - 完整路徑
 * @returns {Object} - { language, path, hasPrefix }
 */
export const parsePathInfo = (pathname) => {
  const hasPrefix = hasLanguagePrefix(pathname);
  const language = hasPrefix ? extractLanguageFromPath(pathname) : DEFAULT_LANGUAGE;
  const path = removeLanguagePrefix(pathname);
  
  return {
    language,
    path,
    hasPrefix,
    fullPath: pathname
  };
};